"use client";
import {
  Box,
  Braces,
  GitFork,
  Play,
  Search,
  X,
} from "lucide-react";
import {
  useMemo,
  useState,
} from "react";
import {
  type ScanReport,
} from "../../../lib/types";
import {
  StatusDot,
} from "../../shared/primitives";


export function GraphView({ report }: { report: ScanReport }) {
  const classMap = useMemo(
    () => new Map(report.classes.map((item) => [item.name, item])),
    [report.classes],
  );
  const normalizedEntryPoints = useMemo(
    () =>
      report.entryPoints.map((entryPoint) => ({
        ...entryPoint,
        className: entryPoint.className.replaceAll("/", "."),
      })),
    [report.entryPoints],
  );
  const entryPointClasses = useMemo(
    () => new Set(normalizedEntryPoints.map((item) => item.className)),
    [normalizedEntryPoints],
  );
  const initialName =
    normalizedEntryPoints.find((item) => classMap.has(item.className))
      ?.className ??
    report.classes.find((item) => item.suspiciousReferences > 0)?.name ??
    report.classes[0]?.name ??
    "";
  const [selectedName, setSelectedName] = useState(initialName);
  const [query, setQuery] = useState("");
  const [externalVisible, setExternalVisible] = useState(true);
  const activeSelectedName = classMap.has(selectedName)
    ? selectedName
    : initialName;
  const selectedClass =
    classMap.get(activeSelectedName) ?? classMap.get(initialName) ?? null;

  const internalEdgeCount = useMemo(
    () =>
      report.classes.reduce(
        (sum, item) =>
          sum +
          new Set(
            item.referencedClasses.filter(
              (reference) =>
                reference !== item.name && classMap.has(reference),
            ),
          ).size,
        0,
      ),
    [classMap, report.classes],
  );

  const incoming = selectedClass
    ? report.classes
        .filter(
          (item) =>
            item.name !== selectedClass.name &&
            item.referencedClasses.includes(selectedClass.name),
        )
        .map((item) => item.name)
    : [];
  const outgoing = selectedClass
    ? Array.from(
        new Set(
          selectedClass.referencedClasses.filter(
            (reference) => reference !== selectedClass.name,
          ),
        ),
      )
    : [];

  const graphNeighbors = (() => {
    const directions = new Map<
      string,
      { incoming: boolean; outgoing: boolean }
    >();
    for (const name of incoming) {
      directions.set(name, { incoming: true, outgoing: false });
    }
    for (const name of outgoing) {
      const current = directions.get(name);
      directions.set(name, {
        incoming: current?.incoming ?? false,
        outgoing: true,
      });
    }
    return Array.from(directions, ([name, direction]) => ({
      name,
      ...direction,
      internal: classMap.has(name),
      suspicious: (classMap.get(name)?.suspiciousReferences ?? 0) > 0,
      entryPoint: entryPointClasses.has(name),
    }))
      .filter((item) => externalVisible || item.internal)
      .sort(
        (left, right) =>
          Number(right.entryPoint) - Number(left.entryPoint) ||
          Number(right.suspicious) - Number(left.suspicious) ||
          Number(right.internal) - Number(left.internal) ||
          left.name.localeCompare(right.name),
      )
      .slice(0, 14);
  })();

  const filteredClasses = report.classes
    .filter((item) =>
      `${item.name} ${item.superClass}`
        .toLowerCase()
        .includes(query.toLowerCase()),
    )
    .slice(0, 500);

  if (!selectedClass) {
    return (
      <div className="entry-empty">
        <GitFork size={34} />
        <strong>No parsed classes</strong>
        <span>A valid JVM class is required to build a reference graph.</span>
      </div>
    );
  }

  const shortLabel = (name: string) => {
    const simple = name.split(".").at(-1) ?? name;
    return simple.length > 24 ? `${simple.slice(0, 21)}…` : simple;
  };
  const packageLabel = (name: string) => {
    const parts = name.split(".");
    parts.pop();
    const value = parts.join(".");
    return value.length > 30 ? `…${value.slice(-27)}` : value || "(default)";
  };

  return (
    <div className="graph-workspace">
      <aside className="graph-sidebar">
        <div className="graph-search">
          <div className="search-field compact">
            <Search size={14} />
            <input
              aria-label="Filter classes in graph"
              placeholder="Find a class"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear">
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        {normalizedEntryPoints.length > 0 && (
          <section className="graph-entrypoints">
            <div className="graph-side-heading">
              <span>ENTRY POINTS</span>
              <small>{normalizedEntryPoints.length}</small>
            </div>
            {normalizedEntryPoints.slice(0, 24).map((entryPoint, index) => {
              const available = classMap.has(entryPoint.className);
              return (
                <button
                  className={
                    selectedClass.name === entryPoint.className
                      ? "is-active"
                      : ""
                  }
                  disabled={!available}
                  key={`${entryPoint.type}-${entryPoint.className}-${index}`}
                  type="button"
                  onClick={() => setSelectedName(entryPoint.className)}
                  title={`${entryPoint.source}${
                    available ? "" : " · class not present in archive"
                  }`}
                >
                  <Play size={11} />
                  <span>
                    <strong>{shortLabel(entryPoint.className)}</strong>
                    <small>
                      {entryPoint.type}
                      {entryPoint.environment
                        ? ` · ${entryPoint.environment}`
                        : ""}
                    </small>
                  </span>
                </button>
              );
            })}
          </section>
        )}
        <section className="graph-class-index">
          <div className="graph-side-heading">
            <span>CLASS INDEX</span>
            <small>{filteredClasses.length}</small>
          </div>
          <div>
            {filteredClasses.map((item) => (
              <button
                className={selectedClass.name === item.name ? "is-active" : ""}
                key={item.path}
                type="button"
                onClick={() => setSelectedName(item.name)}
                title={item.name}
              >
                <Braces size={12} />
                <span>
                  <strong>{item.simpleName}</strong>
                  <small>{item.packageName}</small>
                </span>
                {entryPointClasses.has(item.name) ? (
                  <Play size={10} />
                ) : item.suspiciousReferences > 0 ? (
                  <StatusDot severity="high" />
                ) : null}
              </button>
            ))}
          </div>
        </section>
      </aside>
      <section className="graph-main">
        <header className="graph-header">
          <div>
            <span className="graph-class-icon">
              <GitFork size={18} />
            </span>
            <span>
              <strong>{selectedClass.simpleName}</strong>
              <code>{selectedClass.name}</code>
            </span>
          </div>
          <div className="graph-kpis">
            <span>
              <small>INBOUND</small>
              <strong>{incoming.length}</strong>
            </span>
            <span>
              <small>OUTBOUND</small>
              <strong>{outgoing.length}</strong>
            </span>
            <span>
              <small>INTERNAL EDGES</small>
              <strong>{Math.max(0, internalEdgeCount).toLocaleString()}</strong>
            </span>
            <label className="graph-external-toggle">
              <input
                type="checkbox"
                checked={externalVisible}
                onChange={(event) => setExternalVisible(event.target.checked)}
              />
              <span />
              External
            </label>
          </div>
        </header>
        <div className="reference-canvas">
          {graphNeighbors.length ? (
            <svg
              aria-label={`References connected to ${selectedClass.name}`}
              role="img"
              viewBox="0 0 900 440"
            >
              <defs>
                <marker
                  id="reference-arrow"
                  markerHeight="7"
                  markerWidth="7"
                  orient="auto"
                  refX="6"
                  refY="3.5"
                >
                  <path d="M0,0 L7,3.5 L0,7 Z" />
                </marker>
              </defs>
              {graphNeighbors.map((neighbor, index) => {
                const angle =
                  -Math.PI / 2 +
                  (index * Math.PI * 2) / graphNeighbors.length;
                const x = 450 + Math.cos(angle) * 335;
                const y = 220 + Math.sin(angle) * 155;
                return (
                  <line
                    className={`reference-edge ${
                      neighbor.incoming && neighbor.outgoing
                        ? "is-bidirectional"
                        : neighbor.incoming
                          ? "is-incoming"
                          : "is-outgoing"
                    }`}
                    key={`edge-${neighbor.name}`}
                    markerEnd="url(#reference-arrow)"
                    x1={neighbor.incoming && !neighbor.outgoing ? x : 450}
                    x2={neighbor.incoming && !neighbor.outgoing ? 450 : x}
                    y1={neighbor.incoming && !neighbor.outgoing ? y : 220}
                    y2={neighbor.incoming && !neighbor.outgoing ? 220 : y}
                  />
                );
              })}
              {graphNeighbors.map((neighbor, index) => {
                const angle =
                  -Math.PI / 2 +
                  (index * Math.PI * 2) / graphNeighbors.length;
                const x = 450 + Math.cos(angle) * 335;
                const y = 220 + Math.sin(angle) * 155;
                return (
                  <g
                    className={`reference-node ${
                      neighbor.internal ? "is-internal" : "is-external"
                    } ${neighbor.suspicious ? "is-suspicious" : ""} ${
                      neighbor.entryPoint ? "is-entrypoint" : ""
                    }`}
                    key={neighbor.name}
                    onClick={() =>
                      neighbor.internal && setSelectedName(neighbor.name)
                    }
                    onKeyDown={(event) => {
                      if (
                        neighbor.internal &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        setSelectedName(neighbor.name);
                      }
                    }}
                    role={neighbor.internal ? "button" : undefined}
                    tabIndex={neighbor.internal ? 0 : undefined}
                    transform={`translate(${x - 77} ${y - 26})`}
                  >
                    <rect height="52" rx="5" width="154" />
                    <text className="reference-node-name" x="10" y="21">
                      {shortLabel(neighbor.name)}
                    </text>
                    <text className="reference-node-package" x="10" y="38">
                      {packageLabel(neighbor.name)}
                    </text>
                  </g>
                );
              })}
              <g className="reference-node is-selected" transform="translate(348 187)">
                <rect height="66" rx="7" width="204" />
                <text className="reference-node-name" x="14" y="28">
                  {shortLabel(selectedClass.name)}
                </text>
                <text className="reference-node-package" x="14" y="47">
                  {packageLabel(selectedClass.name)}
                </text>
              </g>
            </svg>
          ) : (
            <div className="reference-empty">
              <GitFork size={30} />
              <strong>No connected references in this view</strong>
              <span>
                Enable external classes or select another class from the index.
              </span>
            </div>
          )}
          <div className="graph-legend">
            <span><i className="legend-inbound" /> inbound</span>
            <span><i className="legend-outbound" /> outbound</span>
            <span><i className="legend-internal" /> archive class</span>
            <span><i className="legend-external" /> runtime / dependency</span>
          </div>
        </div>
        <div className="reference-inspector">
          <section>
            <div>
              <span>INBOUND REFERENCES</span>
              <small>{incoming.length}</small>
            </div>
            {incoming.length ? (
              incoming.slice(0, 80).map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setSelectedName(name)}
                >
                  <Braces size={11} />
                  <code>{name}</code>
                </button>
              ))
            ) : (
              <p>No parsed archive class references this class.</p>
            )}
          </section>
          <section>
            <div>
              <span>OUTBOUND REFERENCES</span>
              <small>{outgoing.length}</small>
            </div>
            {outgoing.length ? (
              outgoing.slice(0, 80).map((name) =>
                classMap.has(name) ? (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setSelectedName(name)}
                  >
                    <Braces size={11} />
                    <code>{name}</code>
                  </button>
                ) : (
                  <span key={name}>
                    <Box size={11} />
                    <code>{name}</code>
                  </span>
                ),
              )
            ) : (
              <p>No class references were recovered from the constant pool.</p>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}
